import type { HTMLAttributes, ReactNode } from 'react';
import styles from './Card.module.css';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  variant?: 'default' | 'wide' | 'form' | 'tinted' | 'warning';
  titleSize?: 'md' | 'lg';
  clickable?: boolean;
  selected?: boolean;
  as?: 'div' | 'section' | 'article';
}

/** The content card pattern from spec 00. */
export function Card({
  title,
  description,
  actions,
  variant = 'default',
  titleSize = 'md',
  clickable,
  selected,
  className,
  children,
  as: Tag = 'section',
  ...rest
}: CardProps) {
  const cls = [
    styles.card,
    variant !== 'default' ? styles[variant] : '',
    clickable ? styles.clickable : '',
    selected ? styles.selected : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tag className={cls} {...rest}>
      {(title || description || actions) && (
        <div className={styles.headerRow}>
          <div className={styles.header}>
            {title && <h3 className={[styles.title, titleSize === 'lg' ? styles.titleLg : ''].join(' ')}>{title}</h3>}
            {description && <p className={styles.description}>{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </Tag>
  );
}

/** Two-column section: `repeat(auto-fit, minmax(340px, 1fr))`. */
export function TwoColumn({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={[styles.grid2, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}
